import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Activity,
    Shield,
    ShieldAlert,
    ShieldCheck,
    AlertTriangle,
    Clock,
    ExternalLink,
    RefreshCw,
    TrendingUp,
    Zap,
    Aperture,
    Target,
    ChevronRight,
    ChevronLeft,
    Search,
    Filter,
    FileDown,
    Lock,
    Radio,
    Server,
    Globe,
    Terminal,
    Copy,
    Check,
    Info,
    Layers,
    SlidersHorizontal,
    Play,
    Pause
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import { backendURL } from '../config';

export default function EventMonitor() {
    const [events, setEvents] = useState([]);
    const [summary, setSummary] = useState({
        critical: 0,
        high: 0,
        medium: 0,
        prevented: 0,
        unmitigated: 0,
        last5mCount: 0,
        spikeDetected: false
    });
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [promoting, setPromoting] = useState(null);
    const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [copiedJson, setCopiedJson] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState("");
    const [filterSeverity, setFilterSeverity] = useState("all");
    const [filterAction, setFilterAction] = useState("all");
    const [filterSourceType, setFilterSourceType] = useState("all");
    const [autoRefreshSecs, setAutoRefreshSecs] = useState(10);
    const [isPaused, setIsPaused] = useState(false);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalEvents, setTotalEvents] = useState(0);

    const token = localStorage.getItem("token");
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchData = useCallback(async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/soc/event-monitor/live`, {
                headers,
                params: {
                    page,
                    limit: 20,
                    severity: filterSeverity,
                    action: filterAction,
                    sourceType: filterSourceType !== "all" ? filterSourceType : undefined,
                    search: searchQuery || undefined
                }
            });
            const { events: newEvents, summary: newSummary, timeline: newTimeline, pagination } = res.data;

            setEvents(newEvents || []);
            setSummary(newSummary || {});
            setTotalPages(pagination?.totalPages || 1);
            setTotalEvents(pagination?.total || 0);

            // Format timeline for 60-minute bucket chart
            if (Array.isArray(newTimeline)) {
                const currentMin = new Date().getMinutes();
                const formattedTimeline = Array.from({ length: 60 }, (_, i) => {
                    const minIdx = (currentMin - (59 - i) + 60) % 60;
                    const minuteData = newTimeline.filter(t => t._id?.minute === minIdx);
                    return {
                        minute: minIdx,
                        label: `${minIdx}m`,
                        critical: minuteData.find(m => m._id?.severity === 'critical')?.count || 0,
                        high: minuteData.find(m => ['error', 'high'].includes(m._id?.severity))?.count || 0,
                        warning: minuteData.find(m => ['warning', 'warn', 'medium'].includes(m._id?.severity))?.count || 0,
                    };
                });
                setTimeline(formattedTimeline);
            }

            setLastUpdateTime(new Date());
        } catch (err) {
            console.error("Failed to fetch event monitor data:", err);
        } finally {
            setLoading(false);
        }
    }, [page, filterSeverity, filterAction, filterSourceType, searchQuery, headers]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Live auto-refresh timer
    useEffect(() => {
        if (isPaused || autoRefreshSecs === 0) return;
        const timer = setInterval(() => {
            fetchData(true);
        }, autoRefreshSecs * 1000);
        return () => clearInterval(timer);
    }, [fetchData, autoRefreshSecs, isPaused]);

    const promoteToIncident = async (event) => {
        setPromoting(event._id);
        try {
            await axios.post(`${backendURL}/api/cases`, {
                title: `Live Escalation: ${event.eventName}`,
                severity: event.severity === 'critical' ? 'Critical' : (event.severity === 'high' ? 'High' : 'Medium'),
                category: event.category || 'Perimeter Threat',
                sourceIp: event.source,
                destIp: event.target,
                port: event.dstPort || 'N/A',
                service: event.service || 'N/A',
                description: `Manually escalated from Live Event Monitor. Threat vector on ${event.deviceName} policy #${event.policyId}. Raw signature: ${event.msg}`
            }, { headers });
            
            alert(`✅ Event successfully escalated to Incident Cases Board!`);
        } catch (err) {
            console.error("Promotion failed:", err);
            alert("Failed to escalate event.");
        } finally {
            setPromoting(null);
        }
    };

    const copyRawJson = (data) => {
        navigator.clipboard.writeText(JSON.stringify(data, null, 2));
        setCopiedJson(true);
        setTimeout(() => setCopiedJson(false), 2000);
    };

    const exportToCSV = () => {
        const csvHeaders = ["Timestamp", "Severity", "Action", "Event Signature", "Category", "Source IP", "Source Port", "Destination IP", "Dest Port", "Service", "Gateway", "Policy ID"];
        const rows = events.map(e => [
            new Date(e.lastSeen).toISOString(),
            e.severity,
            e.action,
            `"${(e.eventName || '').replace(/"/g, '""')}"`,
            `"${e.category}"`,
            e.source,
            e.srcPort || '',
            e.target,
            e.dstPort || '',
            e.service,
            e.deviceName,
            e.policyId
        ]);

        let csvContent = "data:text/csv;charset=utf-8," + [csvHeaders.join(","), ...rows.map(r => r.join(","))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.href = encodedUri;
        link.download = `Live_Telemetry_Stream_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const getSeverityBadge = (sev) => {
        const s = (sev || "").toLowerCase();
        if (s === 'critical') {
            return <span className="px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider bg-rose-500/15 border border-rose-500/30 text-rose-400">CRITICAL</span>;
        }
        if (['error', 'high'].includes(s)) {
            return <span className="px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider bg-amber-500/15 border border-amber-500/30 text-amber-400">HIGH</span>;
        }
        if (['warning', 'warn', 'medium'].includes(s)) {
            return <span className="px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider bg-yellow-500/15 border border-yellow-500/30 text-yellow-400">WARN</span>;
        }
        return <span className="px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider bg-blue-500/15 border border-blue-500/30 text-blue-400">INFO</span>;
    };

    const getActionBadge = (action, isBlocked) => {
        if (isBlocked) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider bg-rose-500/10 border border-rose-500/20 text-rose-400">
                    <Shield className="w-2.5 h-2.5" />
                    BLOCKED
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                <Check className="w-2.5 h-2.5" />
                PASSED
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-[#0B1120] text-slate-300 font-sans p-6 space-y-6">
            
            {/* TACTICAL COMMAND HEADER */}
            <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-6 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 blur-3xl pointer-events-none" />
                
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <div className="w-14 h-14 rounded-2xl bg-blue-600/10 border border-blue-500/30 flex items-center justify-center shadow-[0_0_20px_rgba(37,99,235,0.2)]">
                                <Radio className="w-7 h-7 text-blue-400 animate-pulse" />
                            </div>
                            <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-[#111827] animate-ping" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl font-black text-white italic tracking-tight uppercase leading-none">
                                    LIVE <span className="text-blue-500">EVENT MONITOR</span>
                                </h1>
                                <span className="px-2.5 py-0.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold rounded-full uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                    Live Stream Active
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-1 flex items-center gap-2 font-mono">
                                <Clock className="w-3.5 h-3.5 text-blue-400" />
                                Synchronized at {lastUpdateTime.toLocaleTimeString()} • Processing FortiGate Syslog Telemetry
                            </p>
                        </div>
                    </div>

                    {/* ACTION CONTROLS */}
                    <div className="flex items-center flex-wrap gap-3">
                        {/* Auto-Refresh Selector */}
                        <div className="flex items-center bg-[#1e293b] border border-white/[0.08] rounded-xl p-1 text-xs">
                            <button
                                onClick={() => setIsPaused(!isPaused)}
                                className={`px-2.5 py-1.5 rounded-lg font-bold flex items-center gap-1.5 transition-all ${
                                    isPaused ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-white'
                                }`}
                                title={isPaused ? "Resume Live Stream" : "Pause Stream"}
                            >
                                {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                                {isPaused ? 'PAUSED' : 'LIVE'}
                            </button>
                            <div className="h-4 w-px bg-white/10 mx-1" />
                            {[5, 10, 30].map(s => (
                                <button
                                    key={s}
                                    onClick={() => { setAutoRefreshSecs(s); setIsPaused(false); }}
                                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${
                                        autoRefreshSecs === s && !isPaused ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'
                                    }`}
                                >
                                    {s}s
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => fetchData()}
                            disabled={loading}
                            className="px-4 py-2 bg-[#1e293b] hover:bg-slate-700 border border-white/[0.08] text-slate-200 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-blue-400' : ''}`} />
                            Sync Now
                        </button>

                        <button
                            onClick={exportToCSV}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-2 shadow-[0_0_15px_rgba(37,99,235,0.25)] transition-all cursor-pointer"
                        >
                            <FileDown className="w-3.5 h-3.5" />
                            Export CSV
                        </button>
                    </div>
                </div>

                {/* KPI STRIP */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/[0.06]">
                    <div className="bg-[#1e293b]/60 border border-white/[0.06] rounded-xl p-3.5">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Critical Threats</span>
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-xl font-black text-rose-400 font-mono">{summary.critical}</span>
                            <ShieldAlert className="w-5 h-5 text-rose-400/50" />
                        </div>
                    </div>

                    <div className="bg-[#1e293b]/60 border border-white/[0.06] rounded-xl p-3.5">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">High Risk Probes</span>
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-xl font-black text-amber-400 font-mono">{summary.high}</span>
                            <AlertTriangle className="w-5 h-5 text-amber-400/50" />
                        </div>
                    </div>

                    <div className="bg-[#1e293b]/60 border border-white/[0.06] rounded-xl p-3.5">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Perimeter Drops (1h)</span>
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-xl font-black text-emerald-400 font-mono">{(summary.prevented || 0).toLocaleString()}</span>
                            <ShieldCheck className="w-5 h-5 text-emerald-400/50" />
                        </div>
                    </div>

                    <div className="bg-[#1e293b]/60 border border-white/[0.06] rounded-xl p-3.5">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Stream Velocity</span>
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-xl font-black text-blue-400 font-mono">~{summary.last5mCount || 12}/min</span>
                            <Zap className="w-5 h-5 text-blue-400/50" />
                        </div>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT: TIMELINE + EVENT TABLE */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

                {/* LEFT: 60-MINUTE TACTICAL TIMELINE (4 cols) */}
                <div className="xl:col-span-4 bg-[#111827] border border-white/[0.08] rounded-2xl p-5 shadow-xl flex flex-col justify-between">
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-blue-400" />
                                    Tactical Timeline
                                </h3>
                                <p className="text-[10px] text-slate-400 font-mono mt-0.5">Event Density (Past 60 Minutes)</p>
                            </div>
                            <span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-mono rounded">
                                60-Min Roll
                            </span>
                        </div>

                        <div className="h-64 w-full mt-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={timeline} margin={{ top: 10, right: 0, left: -25, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.6} />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fontSize: 9, fill: '#64748b', fontWeight: 600 }}
                                        axisLine={false}
                                        tickLine={false}
                                        interval={11}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 9, fill: '#64748b', fontWeight: 600 }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }}
                                    />
                                    <Bar dataKey="critical" stackId="a" fill="#f43f5e" radius={[2, 2, 0, 0]} name="Critical" />
                                    <Bar dataKey="high" stackId="a" fill="#f59e0b" name="High" />
                                    <Bar dataKey="warning" stackId="a" fill="#eab308" name="Warning" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-white/[0.06] grid grid-cols-3 gap-2 text-center">
                        <div className="bg-[#1e293b]/40 p-2.5 rounded-lg border border-white/[0.04]">
                            <span className="text-[9px] uppercase font-bold text-slate-500 block">Peak Spike</span>
                            <span className="text-xs font-mono font-bold text-white">
                                {timeline.length > 0 ? Math.max(0, ...timeline.map(t => t.critical + t.high + t.warning)) : 0} logs
                            </span>
                        </div>
                        <div className="bg-[#1e293b]/40 p-2.5 rounded-lg border border-white/[0.04]">
                            <span className="text-[9px] uppercase font-bold text-slate-500 block">Avg Rate</span>
                            <span className="text-xs font-mono font-bold text-blue-400">
                                ~{Math.round((summary.last5mCount || 0) / 5)} /min
                            </span>
                        </div>
                        <div className="bg-[#1e293b]/40 p-2.5 rounded-lg border border-white/[0.04]">
                            <span className="text-[9px] uppercase font-bold text-slate-500 block">Filter Ratio</span>
                            <span className="text-xs font-mono font-bold text-emerald-400">99.4%</span>
                        </div>
                    </div>
                </div>

                {/* RIGHT: REAL-TIME EVENT STREAM (8 cols) */}
                <div className="xl:col-span-8 bg-[#111827] border border-white/[0.08] rounded-2xl p-5 shadow-xl flex flex-col justify-between">
                    
                    {/* TOOLBAR & FILTERS */}
                    <div className="space-y-3 mb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <Radio className="w-4 h-4 text-blue-500 animate-pulse" />
                                <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                                    Real-Time Telemetry Feed
                                </h2>
                                <span className="text-xs text-slate-500 font-mono font-semibold">
                                    ({totalEvents.toLocaleString()} Events Loaded)
                                </span>
                            </div>

                            {/* Search Input */}
                            <div className="relative min-w-[240px]">
                                <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Filter by IP, port, gateway, signature..."
                                    value={searchQuery}
                                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                                    className="w-full bg-[#1e293b] border border-white/[0.08] rounded-xl pl-9 pr-3 py-1.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                                />
                            </div>
                        </div>

                        {/* Filter Pills */}
                        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-white/[0.04]">
                            {/* Severity Filter */}
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1">Severity:</span>
                                {['all', 'critical', 'high', 'warning'].map(sev => (
                                    <button
                                        key={sev}
                                        onClick={() => { setFilterSeverity(sev); setPage(1); }}
                                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all cursor-pointer ${
                                            filterSeverity === sev 
                                                ? 'bg-blue-600 text-white shadow' 
                                                : 'bg-[#1e293b] text-slate-400 hover:text-slate-200 border border-white/[0.04]'
                                        }`}
                                    >
                                        {sev}
                                    </button>
                                ))}
                            </div>

                            {/* Action Filter */}
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1">Action:</span>
                                {[
                                    { id: 'all', label: 'All Actions' },
                                    { id: 'blocked', label: '🛡️ Blocked' },
                                    { id: 'allowed', label: '⚡ Passed' }
                                ].map(act => (
                                    <button
                                        key={act.id}
                                        onClick={() => { setFilterAction(act.id); setPage(1); }}
                                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all cursor-pointer ${
                                            filterAction === act.id 
                                                ? 'bg-blue-600 text-white shadow' 
                                                : 'bg-[#1e293b] text-slate-400 hover:text-slate-200 border border-white/[0.04]'
                                        }`}
                                    >
                                        {act.label}
                                    </button>
                                ))}
                            </div>

                            {/* Telemetry Engine / Vendor Filter */}
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1">Engine:</span>
                                {[
                                    { id: 'all', label: 'All Engines' },
                                    { id: 'fortigate', label: '🔥 FortiGate' },
                                    { id: 'defender', label: '🛡️ Defender XDR' },
                                    { id: 'threatdown', label: '⚡ ThreatDown EDR' }
                                ].map(src => (
                                    <button
                                        key={src.id}
                                        onClick={() => { setFilterSourceType(src.id); setPage(1); }}
                                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all cursor-pointer ${
                                            filterSourceType === src.id 
                                                ? 'bg-blue-600 text-white shadow' 
                                                : 'bg-[#1e293b] text-slate-400 hover:text-slate-200 border border-white/[0.04]'
                                        }`}
                                    >
                                        {src.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* EVENT TABLE */}
                    <div className="overflow-x-auto min-h-[420px]">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead>
                                <tr className="border-b border-white/[0.08] text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                                    <th className="py-2.5 px-3">Status</th>
                                    <th className="py-2.5 px-3">Event Signature / Category</th>
                                    <th className="py-2.5 px-3">Source ➔ Target</th>
                                    <th className="py-2.5 px-3">Gateway / Policy</th>
                                    <th className="py-2.5 px-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {events.map((event) => (
                                    <tr
                                        key={event._id}
                                        onClick={() => setSelectedEvent(event)}
                                        className="hover:bg-blue-600/5 transition-colors cursor-pointer group"
                                    >
                                        <td className="py-3 px-3 align-top whitespace-nowrap">
                                            <div className="flex flex-col gap-1 items-start">
                                                {getSeverityBadge(event.severity)}
                                                {getActionBadge(event.action, event.isBlocked)}
                                            </div>
                                        </td>

                                        <td className="py-3 px-3 align-top max-w-[260px]">
                                            <div className="font-bold text-slate-200 group-hover:text-blue-400 transition-colors leading-tight line-clamp-1">
                                                {event.eventName}
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-1 text-[10px] text-slate-500 font-mono">
                                                <span className="text-blue-400/80 font-bold">{event.category}</span>
                                                <span>•</span>
                                                <span>{new Date(event.lastSeen).toLocaleTimeString()}</span>
                                            </div>
                                        </td>

                                        <td className="py-3 px-3 align-top font-mono text-[11px] whitespace-nowrap">
                                            <div className="flex items-center gap-1.5 text-slate-300">
                                                <span className="text-slate-400">{event.source}{event.srcPort ? `:${event.srcPort}` : ''}</span>
                                                <span className="text-slate-500">➔</span>
                                                <span className="text-slate-200 font-semibold">{event.target}{event.dstPort ? `:${event.dstPort}` : ''}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5 text-[9px] text-slate-500 font-sans">
                                                <span className="px-1.5 py-0.2 bg-[#1e293b] rounded font-mono text-slate-400 font-bold">{event.service || 'IP'}</span>
                                                <span>{event.proto}</span>
                                            </div>
                                        </td>

                                        <td className="py-3 px-3 align-top whitespace-nowrap">
                                            <div className="font-semibold text-slate-300 text-xs flex items-center gap-1.5">
                                                <Server className="w-3 h-3 text-slate-500" />
                                                {event.deviceName}
                                                {event.user && (
                                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-purple-500/10 text-purple-300 border border-purple-500/20">
                                                        👤 {event.user}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[10px] text-slate-500 font-mono mt-0.5 flex items-center gap-1.5">
                                                <span>Policy #{event.policyId} • {event.srcIntf} ➔ {event.dstIntf}</span>
                                                {event.sourceType && (
                                                    <span className="px-1.5 py-0.2 rounded text-[8px] bg-slate-800 text-slate-400 border border-white/[0.04]">
                                                        {event.sourceType}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        <td className="py-3 px-3 align-top text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1.5">
                                                <button
                                                    onClick={() => setSelectedEvent(event)}
                                                    className="p-1.5 bg-[#1e293b] hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                                                    title="Inspect Telemetry Details"
                                                >
                                                    <Info className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => promoteToIncident(event)}
                                                    disabled={promoting === event._id}
                                                    className="p-1.5 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-500/30 rounded-lg transition-colors cursor-pointer"
                                                    title="Escalate to Incident Case"
                                                >
                                                    {promoting === event._id ? (
                                                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <ShieldAlert className="w-3.5 h-3.5" />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                                {events.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan="5" className="py-20 text-center text-slate-500 font-mono">
                                            No events matching the active filter criteria.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION FOOTER */}
                    <div className="pt-4 mt-2 border-t border-white/[0.06] flex items-center justify-between">
                        <span className="text-[11px] text-slate-400 font-mono">
                            Page <span className="text-white font-bold">{page}</span> of <span className="text-white font-bold">{totalPages}</span>
                        </span>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1.5 bg-[#1e293b] hover:bg-slate-700 disabled:opacity-30 text-slate-300 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" /> Prev
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-30 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1 shadow cursor-pointer"
                            >
                                Next <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* EVENT TELEMETRY INSPECTOR MODAL */}
            {selectedEvent && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#111827] border border-white/[0.1] rounded-2xl max-w-2xl w-full p-6 space-y-5 shadow-2xl relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        
                        <div className="flex items-start justify-between border-b border-white/[0.08] pb-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-blue-600/10 border border-blue-500/30 rounded-xl">
                                    <Terminal className="w-5 h-5 text-blue-400" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-white leading-tight">
                                        FortiGate Telemetry Inspector
                                    </h3>
                                    <p className="text-xs text-slate-400 font-mono mt-0.5">
                                        Event ID: {selectedEvent._id}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedEvent(null)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Telemetry Summary Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div className="bg-[#1e293b]/60 p-3 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] font-bold text-slate-500 uppercase block">Severity</span>
                                <span className="font-bold text-rose-400 uppercase mt-0.5 block">{selectedEvent.severity}</span>
                            </div>
                            <div className="bg-[#1e293b]/60 p-3 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] font-bold text-slate-500 uppercase block">Action</span>
                                <span className="font-bold text-amber-400 uppercase mt-0.5 block">{selectedEvent.action}</span>
                            </div>
                            <div className="bg-[#1e293b]/60 p-3 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] font-bold text-slate-500 uppercase block">Gateway</span>
                                <span className="font-semibold text-slate-200 mt-0.5 block truncate">{selectedEvent.deviceName}</span>
                            </div>
                            <div className="bg-[#1e293b]/60 p-3 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] font-bold text-slate-500 uppercase block">Policy ID</span>
                                <span className="font-mono text-blue-400 font-bold mt-0.5 block">#{selectedEvent.policyId}</span>
                            </div>
                        </div>

                        {/* Network Flow & Full Identity Context */}
                        <div className="bg-[#1e293b]/40 border border-white/[0.06] rounded-xl p-4 space-y-2.5 text-xs font-mono">
                            <div className="flex justify-between items-center text-slate-400">
                                <span>Source Host:</span>
                                <span className="text-white font-bold">{selectedEvent.source} {selectedEvent.srcPort ? `(Port ${selectedEvent.srcPort})` : ''}</span>
                            </div>
                            <div className="flex justify-between items-center text-slate-400">
                                <span>Destination:</span>
                                <span className="text-blue-400 font-bold">{selectedEvent.target} {selectedEvent.dstPort ? `(Port ${selectedEvent.dstPort})` : ''}</span>
                            </div>
                            <div className="flex justify-between items-center text-slate-400">
                                <span>User / Account Identity:</span>
                                <span className="text-purple-300 font-bold">{selectedEvent.user || selectedEvent.rawLog?.user || 'Unauthenticated / System'}</span>
                            </div>
                            <div className="flex justify-between items-center text-slate-400">
                                <span>Telemetry Sensor / Engine:</span>
                                <span className="text-emerald-400 font-semibold">{selectedEvent.sourceType || selectedEvent.deviceName}</span>
                            </div>
                            <div className="flex justify-between items-center text-slate-400">
                                <span>Service & Protocol:</span>
                                <span className="text-slate-200">{selectedEvent.service} • {selectedEvent.proto}</span>
                            </div>
                            <div className="flex justify-between items-center text-slate-400">
                                <span>Interface Routing:</span>
                                <span className="text-slate-300">{selectedEvent.srcIntf} ➔ {selectedEvent.dstIntf}</span>
                            </div>
                            <div className="flex justify-between items-start text-slate-400">
                                <span className="whitespace-nowrap">Flag Reason / Trigger:</span>
                                <span className="text-amber-300/90 font-sans font-semibold max-w-sm text-right">{selectedEvent.reason || selectedEvent.msg}</span>
                            </div>
                        </div>

                        {/* Raw Syslog Dump */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-[11px] text-slate-400">
                                <span className="font-bold uppercase tracking-wider">Raw Syslog Packet (JSON)</span>
                                <button
                                    onClick={() => copyRawJson(selectedEvent.rawLog || selectedEvent)}
                                    className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors cursor-pointer"
                                >
                                    {copiedJson ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                                    {copiedJson ? 'Copied' : 'Copy JSON'}
                                </button>
                            </div>
                            <pre className="p-3 bg-[#0B1120] border border-white/[0.08] rounded-xl text-[10px] font-mono text-emerald-400/90 max-h-40 overflow-y-auto custom-scrollbar">
                                {JSON.stringify(selectedEvent.rawLog || selectedEvent, null, 2)}
                            </pre>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-between pt-3 border-t border-white/[0.08]">
                            <button
                                onClick={() => setSelectedEvent(null)}
                                className="px-4 py-2 bg-[#1e293b] hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl transition-colors cursor-pointer"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    promoteToIncident(selectedEvent);
                                    setSelectedEvent(null);
                                }}
                                className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow-[0_0_15px_rgba(37,99,235,0.3)] flex items-center gap-2 cursor-pointer"
                            >
                                <ShieldAlert className="w-4 h-4" />
                                <span>Escalate to Incident Case</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
